import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
} from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { COMPANY } from "@/data/site";
import { NAV_LINKS } from "@/data/navigation";

export default function Footer() {
  const { t, lang } = useLanguage();
  const isRTL = lang === "ar";

  return (
    <footer className="bg-graphite text-white/80">
      <div className="content-container section-padding pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Company Info */}
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-3 font-display font-extrabold text-xl text-white tracking-tight mb-4"
            >
              <img
                src="/images/logo.jpg"
                alt="Sky Mark Co."
                className="h-10 w-10 rounded-lg object-cover border border-white/20"
              />
              <span>
                SKY <span className="text-secondary-blue">MARK</span>
                <span className="text-gold">.</span> CO
              </span>
            </Link>
            <p className="text-sm text-silver leading-relaxed mb-4">
              {COMPANY.description}
            </p>
            <div className={`flex items-start gap-2 text-sm text-silver ${isRTL ? "text-right" : ""}`}>
              <MapPin className={`w-4 h-4 mt-0.5 shrink-0 text-gold ${isRTL ? "order-2" : ""}`} />
              <span>{COMPANY.address.full}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-4">
              {t("quickLinks")}
            </h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-silver hover:text-white transition-colors duration-200"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-4">
              {t("contact")}
            </h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-silver uppercase tracking-wide mb-1">
                  {t("contactPerson")}
                </p>
                <p className="text-sm text-white font-medium">
                  {COMPANY.contactPerson.name}
                </p>
                <p className="text-xs text-silver">
                  {COMPANY.contactPerson.designation}
                </p>
              </div>
              <a
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-2 text-sm text-silver hover:text-white transition-colors duration-200"
              >
                <Phone className="w-4 h-4 text-gold" />
                {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.email.general}`}
                className="flex items-center gap-2 text-sm text-silver hover:text-white transition-colors duration-200"
              >
                <Mail className="w-4 h-4 text-gold" />
                {COMPANY.email.general}
              </a>
              <div className="flex items-start gap-2 text-sm text-silver">
                <Clock className="w-4 h-4 mt-0.5 text-gold" />
                <div>
                  <p>
                    {t("businessHours")}: {COMPANY.hours.days} {COMPANY.hours.time}
                  </p>
                  <p>{t("fridayClosed")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-4">
              {t("getInTouch")}
            </h4>
            <p className="text-sm text-silver mb-4">
              {t("readyToDiscuss")}
            </p>
            <div className="space-y-3">
              <Link
                to="/contact"
                className="block w-full text-center py-2.5 rounded-md bg-secondary-blue text-white font-body font-semibold text-sm transition-all duration-300 hover:bg-primary-deep"
              >
                {t("sendInquiry")}
              </Link>
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md bg-success text-white font-body font-semibold text-sm transition-all duration-300 hover:bg-green-600"
              >
                <MessageCircle className="w-4 h-4" />
                {t("whatsapp")}
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 pt-6 border-t border-white/10 text-center">
          <p className="text-sm text-steel">
            &copy; {new Date().getFullYear()} {COMPANY.name}. {t("copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
